import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, IconDefinition, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Address } from '../../services/customer.address.service';
import AddressSelectionCard from './AddressSelectionCard';
import styles from '../../styles/common/EditAddressDataModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';
import { countryService, CountryFull } from '../../services/country.service';

interface EditAddressDataModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (selectedAddress: Address) => Promise<void>;
    addresses: Address[];
    initialAddressId: string | number | null;
    title: string;
    icon: IconDefinition;
    initialCountry: string;
    isDestination?: boolean;
}

const EditAddressDataModal: React.FC<EditAddressDataModalProps> = ({
    isOpen,
    onClose,
    onSave,
    addresses,
    initialAddressId,
    title,
    icon,
    initialCountry,
    isDestination = false
}) => {
    const [selectedId, setSelectedId] = useState<string | number | null>(initialAddressId);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(initialCountry);
    const [countryList, setCountryList] = useState<CountryFull[]>([]);
    const [countryNamesMap, setCountryNamesMap] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getAllCountries();
                setCountryList(data);
                const map: { [key: string]: string } = {};
                data.forEach((c) => {
                    if (c.iso2Country) {
                        map[c.iso2Country.toUpperCase()] = c.name;
                    }
                });
                setCountryNamesMap(map);
            } catch (err) {
                console.error("Error fetching countries in EditAddressDataModal:", err);
            }
        };
        fetchCountries();
    }, []);

    // Sync state with props when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedId(initialAddressId);
            setSelectedCountry(initialCountry || "");
        }
    }, [isOpen, initialAddressId, initialCountry]);

    const handleSave = async () => {
        const selectedAddress = addresses.find(addr => addr.id === selectedId);
        if (!selectedAddress) return;

        setIsSaving(true);
        try {
            await onSave(selectedAddress);
            onClose();
        } catch (error) {
            console.error("Error saving address selection:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredAddresses = addresses.filter(addr => addr.isO2Country === selectedCountry && (isDestination ? addr.type === "D" : addr.type === "O"));

    const availableCountries = countryList.length > 0 
        ? countryList.filter(c => isDestination ? c.receive : c.send)
        : [];

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            centered
            size="lg"
            className={styles.editAddressDataModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title className={`${styles.modalTitle} ${modalStyles.modalTitle}`}>
                    <FontAwesomeIcon icon={icon} /> {title}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div style={{ marginBottom: '20px' }}>
                    <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#475569',
                        background: '#f8fafc',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        display: 'inline-block',
                        border: '1px solid #e2e8f0'
                    }}>
                        País: <strong style={{ color: '#0f172a', fontWeight: '700' }}>{countryNamesMap[selectedCountry?.toUpperCase()] || selectedCountry || "No seleccionado"}</strong>
                    </div>
                    <select
                        value={selectedCountry}
                        onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setSelectedId(null);
                        }}
                        className="form-select"
                        style={{
                            display: 'none'
                        }}
                    >
                        <option value="">Seleccionar País</option>
                        {availableCountries.map((c) => (
                            <option key={c.id || c.iso2Country} value={c.iso2Country}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.addressGrid}>
                    {!selectedCountry ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '10px', border: '1.5px dashed #cbd5e1', width: '100%' }}>
                            <span style={{ fontSize: '14px' }}>Selecciona un país para ver las direcciones disponibles</span>
                        </div>
                    ) : filteredAddresses.length > 0 ? (
                        filteredAddresses.map((addr) => (
                            <AddressSelectionCard
                                key={addr.id}
                                address={addr}
                                isSelected={selectedId === addr.id}
                                icon={icon}
                                onClick={() => setSelectedId(addr.id)}
                            />
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted w-100">
                            No se encontraron direcciones que coincidan con la búsqueda.
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button className={styles.secondaryBtn} onClick={onClose} disabled={isSaving}>
                    Cancelar
                </button>
                <Button
                    className={modalStyles.closeBtn}
                    onClick={handleSave}
                    disabled={isSaving || !selectedId}
                >
                    {isSaving ? (
                        <><FontAwesomeIcon icon={faSpinner} spin /> Guardando...</>
                    ) : (
                        <><FontAwesomeIcon icon={faCheck} /> Seleccionar</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditAddressDataModal;
